import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  BanknotesIcon, ArrowTrendingUpIcon, ArrowTrendingDownIcon,
  BuildingOfficeIcon, ClockIcon, ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';

const fmt      = n => `Rs. ${Math.round(n||0).toLocaleString('en-PK')}`;
const fmtDate  = d => d ? new Date(d).toLocaleDateString('en-PK',{day:'numeric',month:'short',year:'numeric'}) : '—';

const PLAN_COLORS = {
  free_trial:'#f59e0b', basic:'#3b6ef8', pro:'#10c98e',
  enterprise:'#8b5cf6', custom:'#0d9488', suspended:'#ef4444',
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .sb * { box-sizing:border-box; }
  .sb { font-family:'Sora',sans-serif; color:#1a1d2e; animation:sbFade .4s ease both; }
  @keyframes sbFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .sb-title { font-size:1.35rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; margin-bottom:4px; }
  .sb-sub   { font-size:.76rem; color:#9094a8; margin-bottom:2rem; }
  .sb-stat-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:1rem; margin-bottom:1.8rem; }
  .sb-stat { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.3rem 1.4rem; position:relative; overflow:hidden; }
  .sb-stat::after { content:''; position:absolute; top:0; right:0; width:70px; height:70px; border-radius:0 16px 0 70px; opacity:.05; }
  .sb-stat.green::after{background:#10c98e} .sb-stat.red::after{background:#ef4444} .sb-stat.blue::after{background:#3b6ef8}
  .sb-stat-icon { width:36px; height:36px; border-radius:10px; display:flex; align-items:center; justify-content:center; margin-bottom:.8rem; }
  .sb-stat-icon svg { width:17px; height:17px; stroke-width:1.8; }
  .sb-stat-icon.green{background:#ecfdf5;color:#10c98e} .sb-stat-icon.red{background:#fef2f2;color:#ef4444} .sb-stat-icon.blue{background:#eef2ff;color:#3b6ef8}
  .sb-stat-label { font-size:.68rem; font-weight:600; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .sb-stat-val   { font-size:1.4rem; font-weight:700; color:#0f1123; font-family:'JetBrains Mono',monospace; line-height:1.2; margin-top:2px; }
  .sb-stat-sub   { font-size:.7rem; color:#9094a8; margin-top:4px; }
  .sb-stat-yr    { font-size:.72rem; font-weight:600; color:#b0b5cc; margin-top:5px; font-family:'JetBrains Mono',monospace; }
  .sb-two { display:grid; grid-template-columns:1.4fr 1fr; gap:1.2rem; margin-bottom:1.5rem; }
  .sb-card { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.4rem; }
  .sb-card-title { font-size:.9rem; font-weight:700; color:#0f1123; margin-bottom:.3rem; }
  .sb-card-sub   { font-size:.72rem; color:#9094a8; margin-bottom:1.2rem; }
  .plan-dist { display:flex; flex-direction:column; gap:.5rem; }
  .plan-row  { display:flex; align-items:center; gap:10px; }
  .plan-label { font-size:.78rem; font-weight:600; color:#1a1d2e; width:90px; flex-shrink:0; text-transform:capitalize; }
  .plan-bar-wrap { flex:1; height:8px; background:#f0f2f7; border-radius:99px; overflow:hidden; }
  .plan-bar { height:100%; border-radius:99px; transition:width .5s ease; }
  .plan-count { font-size:.72rem; font-weight:600; color:#9094a8; min-width:24px; text-align:right; font-family:'JetBrains Mono',monospace; }
  .expiring-list { display:flex; flex-direction:column; gap:.6rem; max-height:280px; overflow-y:auto; }
  .exp-row { display:flex; align-items:center; gap:10px; padding:8px 10px; background:#fffbeb; border:1px solid #fcd34d; border-radius:10px; }
  .exp-icon { width:28px; height:28px; border-radius:8px; background:#fef3c7; color:#f59e0b; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .exp-icon svg { width:13px; height:13px; stroke-width:2; }
  .exp-school { font-size:.8rem; font-weight:600; color:#0f1123; }
  .exp-date   { font-size:.68rem; color:#92400e; margin-top:2px; }
  .exp-plan   { font-size:.65rem; font-weight:700; padding:2px 8px; border-radius:99px; background:#fef3c7; color:#92400e; }
  .sb-bottom { display:grid; grid-template-columns:1fr 1fr; gap:1.2rem; }
  .cost-breakdown { display:flex; flex-direction:column; gap:.5rem; margin-top:.5rem; }
  .cost-row { display:flex; align-items:center; justify-content:space-between; padding:8px 10px; background:#f8f9fc; border-radius:9px; }
  .cost-name  { font-size:.78rem; color:#1a1d2e; font-weight:500; }
  .cost-amount { font-size:.78rem; font-weight:600; font-family:'JetBrains Mono',monospace; }
  .loading { display:flex; align-items:center; justify-content:center; height:300px; gap:10px; color:#9094a8; }
  .spin { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#3b6ef8; border-radius:50%; animation:sp .7s linear infinite; }
  @keyframes sp { to{transform:rotate(360deg)} }
  .custom-tip { background:white; border:1px solid #e8ebf4; border-radius:10px; padding:10px 14px; font-family:'Sora',sans-serif; font-size:.76rem; box-shadow:0 4px 14px rgba(0,0,0,.1); }
  .custom-tip .lbl { font-weight:700; color:#0f1123; margin-bottom:4px; }
  @media (max-width:1100px) { .sb-two { grid-template-columns:1fr; } .sb-bottom { grid-template-columns:1fr; } }
  @media (max-width:768px)  { .sb-stat-grid { grid-template-columns:1fr 1fr; } }
`;

// Generate last 6 months labels
const getLast6Months = () => {
  const months = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date();
    d.setMonth(d.getMonth() - i);
    months.push(d.toLocaleDateString('en-US',{month:'short',year:'2-digit'}));
  }
  return months;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="custom-tip">
      <div className="lbl">{label}</div>
      {payload.map(p => <div key={p.dataKey} style={{color:p.color}}>{p.name}: <strong>{fmt(p.value)}</strong></div>)}
    </div>
  );
};

export default function SuperBilling() {
  const navigate  = useNavigate();
  const [data,    setData]    = useState(null);
  const [services,setServices]= useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/super/billing/overview'),
      api.get('/super/services'),
    ]).then(([b, s]) => {
      setData(b.data);
      setServices(s.data || []);
    }).catch(() => toast.error('Failed to load'))
    .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="sb"><style>{css}</style><div className="loading"><div className="spin"/>Loading…</div></div>;

  const months     = getLast6Months();
  const byPlan     = data?.byPlan || {};
  const totalPlan  = Object.values(byPlan).reduce((s,v) => s+v, 0);

  // Mock trend data (real data would need monthly aggregation)
  const trendData = months.map((m, i) => ({
    month: m,
    revenue: Math.round((data?.revenue?.monthly||0) * (0.7 + i * 0.06)),
    costs:   Math.round((data?.costs?.monthly||0)   * (0.85 + i * 0.03)),
  }));

  const monthlyCosts = services.filter(s => s.isActive).map(s => ({
    name:   s.name,
    amount: s.billingCycle==='yearly' ? s.ourYearlyCost/12 : s.ourMonthlyCost,
    color:  '#ef4444',
  })).filter(s => s.amount > 0).sort((a,b) => b.amount-a.amount).slice(0,8);

  return (
    <div className="sb"><style>{css}</style>
      <div className="sb-title">Billing Overview</div>
      <div className="sb-sub">Revenue, costs and profit across all school subscriptions.</div>

      {/* Top stat cards */}
      <div className="sb-stat-grid">
        <div className="sb-stat green">
          <div className="sb-stat-icon green"><ArrowTrendingUpIcon /></div>
          <div className="sb-stat-label">Monthly Revenue</div>
          <div className="sb-stat-val">{fmt(data?.revenue?.monthly)}</div>
          <div className="sb-stat-yr">Yearly est: {fmt(data?.revenue?.yearly)}</div>
          <div className="sb-stat-sub">Total collected: {fmt(data?.revenue?.totalCollected)}</div>
        </div>
        <div className="sb-stat red">
          <div className="sb-stat-icon red"><ArrowTrendingDownIcon /></div>
          <div className="sb-stat-label">Monthly Costs</div>
          <div className="sb-stat-val">{fmt(data?.costs?.monthly)}</div>
          <div className="sb-stat-yr">Yearly est: {fmt(data?.costs?.yearly)}</div>
          <div className="sb-stat-sub">Infrastructure & services</div>
        </div>
        <div className="sb-stat blue" style={{border: (data?.profit?.monthly||0)>=0 ? '1px solid #e8ebf4' : '1px solid #fecaca'}}>
          <div className="sb-stat-icon" style={{background:(data?.profit?.monthly||0)>=0?'#ecfdf5':'#fef2f2', color:(data?.profit?.monthly||0)>=0?'#10c98e':'#ef4444'}}>
            <BanknotesIcon />
          </div>
          <div className="sb-stat-label">Monthly Profit</div>
          <div className="sb-stat-val" style={{color:(data?.profit?.monthly||0)>=0?'#10c98e':'#ef4444'}}>
            {fmt(data?.profit?.monthly)}
          </div>
          <div className="sb-stat-yr">Revenue − Costs</div>
          <div className="sb-stat-sub" style={{color:(data?.profit?.monthly||0)>=0?'#10c98e':'#ef4444'}}>
            {(data?.profit?.monthly||0) >= 0 ? '↑ Profitable' : '↓ Running at loss'}
          </div>
        </div>
      </div>

      {/* Revenue / Cost trend + Plan distribution */}
      <div className="sb-two">
        <div className="sb-card">
          <div className="sb-card-title">Revenue vs Costs (6 Months)</div>
          <div className="sb-card-sub">Estimated trend based on current subscriptions</div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={trendData} margin={{top:4,right:10,left:0,bottom:0}}>
              <defs>
                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#10c98e" stopOpacity={0.15}/>
                  <stop offset="95%" stopColor="#10c98e" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="costGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#ef4444" stopOpacity={0.1}/>
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f8" />
              <XAxis dataKey="month" tick={{fontSize:11,fontFamily:'Sora'}} />
              <YAxis tick={{fontSize:11,fontFamily:'Sora'}} tickFormatter={v=>`Rs.${(v/1000).toFixed(0)}k`} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#10c98e" fill="url(#revGrad)" strokeWidth={2} />
              <Area type="monotone" dataKey="costs"   name="Costs"   stroke="#ef4444" fill="url(#costGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="sb-card">
          <div className="sb-card-title">Plan Distribution</div>
          <div className="sb-card-sub">{totalPlan} active subscriptions</div>
          <div className="plan-dist">
            {Object.entries(byPlan).filter(([,v])=>v>0).map(([plan, count]) => (
              <div key={plan} className="plan-row">
                <div className="plan-label" style={{color:PLAN_COLORS[plan]||'#9094a8'}}>{plan.replace('_',' ')}</div>
                <div className="plan-bar-wrap">
                  <div className="plan-bar" style={{width:`${totalPlan?Math.round(count/totalPlan*100):0}%`,background:PLAN_COLORS[plan]||'#9094a8'}} />
                </div>
                <div className="plan-count">{count}</div>
              </div>
            ))}
            {totalPlan === 0 && <div style={{color:'#9094a8',fontSize:'.8rem'}}>No subscriptions yet.</div>}
          </div>
        </div>
      </div>

      {/* Expiring soon + Cost breakdown */}
      <div className="sb-bottom">
        <div className="sb-card">
          <div className="sb-card-title" style={{display:'flex',alignItems:'center',gap:8}}>
            <ExclamationTriangleIcon style={{width:16,height:16,color:'#f59e0b',strokeWidth:2}} />
            Expiring Soon
          </div>
          <div className="sb-card-sub">Subscriptions expiring in the next 7 days</div>
          <div className="expiring-list">
            {(data?.expiringSoon||[]).length === 0 ? (
              <div style={{color:'#9094a8',fontSize:'.8rem',padding:'.5rem 0'}}>✅ No subscriptions expiring soon.</div>
            ) : (data?.expiringSoon||[]).map((s, i) => (
              <div key={i} className="exp-row">
                <div className="exp-icon"><ExclamationTriangleIcon /></div>
                <div style={{flex:1}}>
                  <div className="exp-school">{s.school}</div>
                  <div className="exp-date">Expires {fmtDate(s.endDate)}</div>
                </div>
                <span className="exp-plan">{s.plan?.replace('_',' ')}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="sb-card">
          <div className="sb-card-title">Monthly Cost Breakdown</div>
          <div className="sb-card-sub">What we pay for infrastructure</div>
          <div className="cost-breakdown">
            {monthlyCosts.length === 0 ? (
              <div style={{color:'#9094a8',fontSize:'.8rem'}}>No services added yet.</div>
            ) : monthlyCosts.map((c, i) => (
              <div key={i} className="cost-row">
                <div className="cost-name">{c.name}</div>
                <div className="cost-amount" style={{color:'#ef4444'}}>{fmt(c.amount)}/mo</div>
              </div>
            ))}
            {monthlyCosts.length > 0 && (
              <div className="cost-row" style={{background:'#fef2f2',marginTop:4}}>
                <div className="cost-name" style={{fontWeight:700,color:'#0f1123'}}>Total</div>
                <div className="cost-amount" style={{color:'#ef4444',fontSize:'.84rem'}}>{fmt(data?.costs?.monthly)}/mo</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}